import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, CheckIcon } from "lucide-react";
import { useHackathon } from "../contexts/HackathonContext";
import { milestones } from "../data/program";
import { schedule } from "../data/schedule";
import { Avatar } from "../components/Avatar";
import { VideoUploadSlot } from "../components/VideoUploadSlot";
import { ProductBriefForm } from "../components/ProductBriefForm";
import { groupProgress } from "../utils/progress";
import { subjectForGroup } from "../utils/subjects";
import { currentProgramDay, PROGRAM_DAYS } from "../utils/dates";
export function GroupDetail() {
  const {
    id
  } = useParams();
  const groupId = Number(id);
  const {
    state,
    toggleMilestone
  } = useHackathon();
  const group = state.groups.find(g => g.id === groupId);
  if (!group) {
    return <main className="mx-auto max-w-6xl px-5 py-16 text-center md:px-8" data-id="element-824" data-mp="151dnaic1fv6h">
        <p className="text-lg font-semibold" data-id="element-825" data-mp="1007wwr5uok04">Group not found</p>
        <Link to="/organizer/groups" className="mt-2 inline-block text-sm text-muted underline" data-id="element-826" data-mp="xix0ucxzmxg8">
          Back to groups
        </Link>
      </main>;
  }
  const members = state.students.filter(s => s.groupId === groupId);
  const subject = subjectForGroup(groupId);
  const progress = groupProgress(group, state.videos, groupId);
  const today = currentProgramDay();
  return <main className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-12" data-id="element-827" data-mp="esyqtramqu9y">
      <Link to="/organizer/groups" className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors duration-150 hover:text-ink" data-id="element-828" data-mp="ernn1rh6pmiu">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-829" data-mp="4vawh2gxrosb" />
        All groups
      </Link>

      <header className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-id="element-830" data-mp="9tci9v5qy27h">
        <div data-id="element-831" data-mp="3ryyx49b7lwau">
          <p className="text-sm font-semibold text-saffron-ink" data-id="element-832" data-mp="3rz8r07zdknlt">{subject ?? "Track not assigned yet"}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight md:text-4xl" data-id="element-833" data-mp="3w00zgz0faoh1">
            Group {groupId}
            {group.productName && <span className="text-muted" data-id="element-834" data-mp="22joamdk91hlq"> · {group.productName}</span>}
          </h1>
          {group.tagline && <p className="mt-2 max-w-2xl text-[15px] text-muted" data-id="element-835" data-mp="1xpjylfw4mjds">{group.tagline}</p>}
        </div>
        <div className="flex items-center gap-4" data-id="element-836" data-mp="1sstnz833cfzr">
          <div className="text-right" data-id="element-837" data-mp="1nu8iou8zf64g">
            <p className="text-3xl font-bold tabular-nums" data-id="element-838" data-mp="1nva863qqvv5p">{progress.percent}%</p>
            <p className="text-xs text-muted" data-id="element-839" data-mp="2lfwi7o0qi4k7">
              {progress.milestonesDone}/{progress.milestonesTotal} milestones · {progress.videosDone}/{progress.videosTotal} videos
            </p>
          </div>
        </div>
      </header>

      <div className="mt-10 grid gap-10 lg:grid-cols-3" data-id="element-840" data-mp="2bkw6yq37g8d6">
        <div className="space-y-12 lg:col-span-2" data-id="element-841" data-mp="27gocsddza07k">
          <section aria-labelledby="videos-heading" data-id="element-842" data-mp="103wffwoolxk7">
            <div className="flex items-baseline justify-between pb-3" data-id="element-843" data-mp="105fvbm6fe6lg">
              <h2 id="videos-heading" className="text-lg font-semibold" data-id="element-844" data-mp="w21dcpyxfg5d">
                Daily progress videos
              </h2>
              <p className="text-sm text-muted" data-id="element-845" data-mp="37r7n6p4a2a3h">One per day · due 21:00</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-id="element-846" data-mp="3bs16142v4ypi">
              {Array.from({
              length: PROGRAM_DAYS
            }, (_, i) => {
              const day = i + 1;
              return <VideoUploadSlot key={day} groupId={groupId} day={day} entry={state.videos.find(v => v.groupId === groupId && v.day === day)} locked={day > today} isToday={day === today} prompt={schedule.find(s => s.day === day)?.videoPrompt} data-id="element-847" data-mp="3lht3q769e7y7" />;
            })}
            </div>
          </section>

          <section aria-labelledby="brief-heading" data-id="element-848" data-mp="253ks37n11j1c">
            <h2 id="brief-heading" className="pb-3 text-lg font-semibold" data-id="element-849" data-mp="2555pd50q7g2w">
              Product brief
            </h2>
            <div className="rounded-xl border border-line bg-surface p-5" data-id="element-850" data-mp="1erfulrydtuci">
              <ProductBriefForm group={group} data-id="element-851" data-mp="1etitl1hiixtd" />
            </div>
          </section>
        </div>

        <aside className="space-y-10" data-id="element-852" data-mp="19uz5ovjczbyd">
          <section aria-labelledby="team-heading" data-id="element-853" data-mp="1oq5d9my3ritt">
            <h2 id="team-heading" className="pb-3 text-lg font-semibold" data-id="element-854" data-mp="1onsw3c065cht">
              Team
            </h2>
            <ul className="divide-y divide-line rounded-xl border border-line bg-surface" data-id="element-855" data-mp="1joy5vozvglno">
              {members.map(m => <li key={m.id} className="flex items-center gap-3 px-4 py-3" data-id="element-856" data-mp="r2n3c8spgao8">
                  <Avatar name={m.name} school={m.school} photo={m.photo} data-id="element-857" data-mp="15r16kku1a15b" />
                  <div className="min-w-0 flex-1" data-id="element-858" data-mp="15t44rs5cb0l4">
                    <Link to={`/organizer/people/${m.id}`} className="block truncate text-[15px] font-medium hover:underline" data-id="element-859" data-mp="11017b8xfz6zn">
                      {m.name}
                    </Link>
                    <p className="truncate text-xs text-muted" data-id="element-860" data-mp="8c5ewgewo18o">
                      {m.role || (m.school === "link" ? "LINK · Brazil" : "Mahindra · India")}
                    </p>
                  </div>
                </li>)}
            </ul>
          </section>

          {state.mentors.some(m => m.groupIds.includes(groupId)) && <section aria-labelledby="mentors-heading" data-id="element-861" data-mp="2a2mcp3f1k4yg">
              <h2 id="mentors-heading" className="pb-3 text-lg font-semibold" data-id="element-862" data-mp="2ez4h226gm1a9">
                Mentors
              </h2>
              <ul className="space-y-3" data-id="element-863" data-mp="32qrzdvpno40x">
                {state.mentors.filter(m => m.groupIds.includes(groupId)).map(m => <li key={m.id} data-id="element-864" data-mp="2tpbs3wu50i0x">
                      <Link to={`/organizer/people/${m.id}`} className="flex items-center gap-3 hover:underline" data-id="element-865" data-mp="2to8wxcps7tf7">
                        <Avatar name={m.name} photo={m.photo} size="sm" data-id="element-866" data-mp="2xsgqpob3jzka" />
                        <span className="min-w-0" data-id="element-867" data-mp="1i8tzy8xkp1k3">
                          <span className="block truncate text-sm font-medium" data-id="element-868" data-mp="1mayurazrc48b">{m.name}</span>
                          {m.expertise && <span className="block truncate text-xs text-muted" data-id="element-869" data-mp="3oyfatxnr7n5h">{m.expertise}</span>}
                        </span>
                      </Link>
                    </li>)}
              </ul>
            </section>}

          <section aria-labelledby="milestones-heading" data-id="element-870" data-mp="3f69ksth2bfsx">
            <h2 id="milestones-heading" className="pb-3 text-lg font-semibold" data-id="element-871" data-mp="3a9rj62h13xkt">
              Milestones
            </h2>
            <ul className="space-y-1" data-id="element-872" data-mp="3685wjpep04xf">
              {milestones.map(m => {
              const done = !!group.milestones[m.id];
              return <li key={m.id} data-id="element-873" data-mp="2w8yonhqq92hp">
                    <button type="button" role="checkbox" aria-checked={done} onClick={() => toggleMilestone(groupId, m.id)} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-150 hover:bg-surface" data-id="element-874" data-mp="2w9zyxjzx5kyd">
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors duration-150 ${done ? "border-brgreen bg-brgreen text-white" : "border-ink/25 bg-surface"}`} data-id="element-875" data-mp="y0klz5xqlwu9">
                        {done && <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-876" data-mp="1bqo53o1blafh" />}
                      </span>
                      <span className={`flex-1 text-sm ${done ? "text-muted line-through" : ""}`} data-id="element-877" data-mp="f2q4e3ijmd09">{m.label}</span>
                      <span className="text-xs text-muted" data-id="element-878" data-mp="j4bq8ed221ml">Day {m.day}</span>
                    </button>
                  </li>;
            })}
            </ul>
          </section>
        </aside>
      </div>


    </main>;
}